import { useState } from 'react'

import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SimpleDivider } from '@tribeplatform/react-ui-kit/Divider'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import { T, useI18n } from '../../i18n/index.js'
import { LinkPicker } from '../../Picker/LinkPicker.js'
import { SortableItemList } from '../../SortableItemList/SortableItemList.js'
import { useMultiViewBlock } from '../hooks/useMultiViewBlock.js'
import type { StaticField, View, ViewSettingsComponent } from '../types.js'

import { GenericSettings } from './GenericSettings.js'

interface Props<T extends View> {
  name: string
  genericView: T
  staticFields: StaticField[]
  viewSettingsComponent: ViewSettingsComponent<T>
  deactivateBlock: () => void
}
export function MultiViewBlockSettings<T extends View>({
  name,
  genericView,
  staticFields,
  viewSettingsComponent: ViewSettings,
  deactivateBlock,
}: Props<T>) {
  const { $t } = useI18n()
  const {
    selectedViewId,
    showViewSettings,
    hideViewSettings,
    setViews,
    addView,
    setTitle,
    setDescription,
    setProperties,
    setLinkText,
    setLinkUrl,
    setHeadingLink,
    title,
    description,
    linkText,
    linkUrl,
    views,
    compiledViews,
  } = useMultiViewBlock({ genericView, staticFields })
  const [headingLinkError, setHeadingLinkError] = useState(false)

  return (
    <>
      <GenericSettings title={name} goBack={deactivateBlock}>
        {!selectedViewId && (
          <div className="space-y-5">
            <FormControl.Input
              name="title"
              label={<T defaultMessage="Title" id="Generics.Title" />}
              placeholder={$t({
                defaultMessage: "What's this block all about?",
                id: 'MultiViewBlock.Title.PlaceHolder',
              })}
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
            <FormControl.Textarea
              name="description"
              label={
                <T defaultMessage="Description" id="Generics.Description" />
              }
              placeholder={$t({
                defaultMessage: 'Enter a description...',
                id: 'MultiViewBlock.Description.PlaceHolder',
              })}
              value={description}
              className="resize-none"
              onChange={e => setDescription(e.target.value)}
            />
            <SimpleDivider variant="subdued" />
            <Toggle
              title={
                <T
                  defaultMessage="Display link in heading"
                  id="MultiViewBlock.DisplayLinkInHeading"
                />
              }
              checked={!!linkText}
              onChange={value => {
                if (value) {
                  setHeadingLink(
                    $t({
                      defaultMessage: 'Link text',
                      id: 'MultiViewBlock.LinkText',
                    }),
                    '',
                  )
                  return
                }

                setHeadingLink('', '')
                setHeadingLinkError(false)
              }}
            />
            {!!linkText && (
              <Card className="bg-surface-subdued" padding="sm" transparent>
                <Card.Content>
                  <div className="space-y-3">
                    <FormControl.Input
                      name="linkText"
                      label={$t({
                        defaultMessage: 'Link text',
                        id: 'Generics.LinkText',
                      })}
                      value={headingLinkError ? '' : linkText}
                      invalid={headingLinkError}
                      error={$t({
                        defaultMessage:
                          'Please provide a text for the heading link',
                        id: 'MultiViewBlock.Validation.HeadingLinkTextRequired',
                      })}
                      onChange={e => {
                        const { value } = e.target

                        if (!value || !value.trim()) {
                          setHeadingLinkError(true)
                        } else {
                          setHeadingLinkError(false)
                          setLinkText(value)
                        }
                      }}
                    />
                    <LinkPicker
                      name="linkUrl"
                      label={$t({
                        defaultMessage: 'URL',
                        id: 'MultiViewBlock.LinkURL',
                      })}
                      value={linkUrl}
                      onChange={newLink => setLinkUrl(newLink)}
                    />
                  </div>
                </Card.Content>
              </Card>
            )}
            <SimpleDivider variant="subdued" />
            <FormControl.Label>
              <T defaultMessage="Tab views" id="MultiViewBlock.TabViews.Noun" />
            </FormControl.Label>
            <SortableItemList
              items={views}
              setItems={setViews}
              onClick={showViewSettings}
              addItem={() =>
                addView(
                  $t({
                    defaultMessage: 'New view',
                    id: 'MultiViewBlock.NewView',
                  }),
                )
              }
              buttonText={$t({
                defaultMessage: 'Add view',
                id: 'MultiViewBlock.AddView',
              })}
            />
          </div>
        )}
      </GenericSettings>
      {!!selectedViewId && (
        <ViewSettings
          view={views.find(view => view.id === selectedViewId)}
          compiledView={compiledViews.find(view => view.id === selectedViewId)}
          setProperties={setProperties}
          goBack={hideViewSettings}
        />
      )}
    </>
  )
}
